import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import UrlForm from "@/components/UrlForm";
import Seo, { API_ORIGIN } from "@/components/Seo";
import Banner from "@/components/Banner";
import GoogleAd from "@/components/GoogleAd";
import {
  organizationJsonLd,
  websiteJsonLd,
  webApplicationJsonLd,
  faqJsonLd,
} from "@/lib/seo-data";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ShieldCheck, Search, FileText, AlertTriangle,
  CheckCircle2, Clock, Lock, ChevronRight,
  Star, TrendingUp, Shield, Eye, Scale, MousePointerClick,
  Activity
} from "lucide-react";

// ─── Score convention: 0 = very suspicious, 100 = trustworthy ───────────────
// Red < 40 | Amber 40–69 | Green ≥ 70

const FAQ_ITEMS = [
  {
    q: "Comment fonctionne l'analyse ?",
    a: "Nous visitons le site que vous indiquez et analysons automatiquement une série de signaux : HTTPS, mentions légales, CGV, coordonnées, informations société, qualité du contenu, etc. Un score de risque est ensuite calculé à partir de ces données.",
  },
  {
    q: "Vos résultats sont-ils fiables à 100% ?",
    a: "Non, et nous le disons clairement. Notre outil est une aide à la décision basée sur des signaux automatiquement détectés. Les données peuvent être incomplètes, et l'absence d'un signal ne garantit pas la fiabilité d'un site. Complétez toujours votre propre vérification.",
  },
  {
    q: "Un score proche de 100 garantit-il que le site est sûr ?",
    a: "Non. Un score proche de 100 signifie que notre analyse n'a pas détecté de signal majeur de vigilance. Cela ne garantit pas l'absence de tout risque — restez toujours vigilant.",
  },
  {
    q: "Que signifie un score proche de 0 ?",
    a: "Un score proche de 0 signifie que plusieurs signaux de vigilance ont été détectés. Cela peut indiquer un site risqué — évitez d'acheter sans vérification approfondie.",
  },
  {
    q: "Mes données sont-elles conservées ?",
    a: "L'URL analysée et les résultats de l'analyse sont enregistrés pour améliorer notre service. Aucune donnée personnelle vous concernant n'est collectée lors d'une analyse. Consultez notre politique de confidentialité pour plus d'informations.",
  },
  {
    q: "Fiable ou Pas est-il un outil officiel ?",
    a: "Non. Fiable ou Pas est un outil d'aide à la décision indépendant. Nous ne sommes pas une autorité légale ou officielle. En cas de fraude avérée, contactez SignalConso ou les autorités compétentes.",
  },
];

// ─── Ticker items — score 0=very suspicious, 100=trustworthy ────────────────
// All domains are clearly fictional — no real brands.
const TICKER_ITEMS = [
  { url: "promo-flash-deals.fr",      score: 26 },
  { url: "boutique-verified-shop.fr", score: 91 },
  { url: "discount-tech99.eu",        score: 48 },
  { url: "soldes-urgent.shop",        score: 9  },
  { url: "monmagasin-secure.fr",      score: 88 },
  { url: "luxe-outlet-paris.fr",      score: 37 },
  { url: "nike-officiel-store.net",   score: 18 },
  { url: "e-commerce-fiable.fr",      score: 94 },
  { url: "tendance-mode2024.fr",      score: 43 },
];

function tickerColor(score) {
  if (score >= 70) return "score-safe";
  if (score >= 40) return "score-warn";
  return "score-danger";
}

function ScoreRing({ score }) {
  // score 0–100, 0=very suspicious(red), 100=trustworthy(green)
  const r = 30;
  const circ = 2 * Math.PI * r;
  const filled = (score / 100) * circ;
  const offset = circ - filled;
  const color = score >= 70 ? "#1A6641" : score >= 40 ? "#D97706" : "#D94040";

  return (
    <div className="relative flex-shrink-0" style={{ width: 80, height: 80 }}>
      <svg width="80" height="80" viewBox="0 0 80 80" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="40" cy="40" r={r} fill="none" stroke="#F0F4F8" strokeWidth="7" />
        <circle
          cx="40" cy="40" r={r}
          fill="none"
          stroke={color}
          strokeWidth="7"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-extrabold leading-none" style={{ fontFamily: "'Syne', sans-serif", color }}>
          {score}
        </span>
        <span className="text-[9px] text-slate-400 mt-0.5">/ 100</span>
      </div>
    </div>
  );
}

function ScorePreview() {
  const signals = [
    { ok: true,    label: "HTTPS actif" },
    { ok: false,   label: "Mentions légales absentes" },
    { ok: false,   label: "CGV introuvables" },
    { ok: "warn",  label: "Domaine récent (< 6 mois)" },
    { ok: false,   label: "Aucun contact identifiable" },
  ];

  return (
    <div className="card-base overflow-hidden shadow-md" data-testid="score-preview">
      {/* Bar */}
      <div className="flex items-center gap-2 px-4 py-3 bg-[#F7F9FC] border-b border-[#E4EAF0] text-xs text-[#3D5166]">
        <span className="inline-block w-2 h-2 rounded-full bg-[#22c55e] animate-pulse" />
        <span className="text-[11px] tracking-wide">Analyse ·</span>
        <span className="font-medium bg-white border border-[#E4EAF0] rounded-md px-2 py-0.5 text-[#0E1621]">
          suspect-shop.fr
        </span>
      </div>

      {/* Body */}
      <div className="flex items-start gap-5 p-5">
        <ScoreRing score={28} />
        <div className="flex flex-col gap-2 flex-1">
          {signals.map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-[#3D5166]">
              <span
                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  s.ok === true ? "bg-[#22c55e]" : s.ok === "warn" ? "bg-[#f59e0b]" : "bg-[#ef4444]"
                }`}
              />
              {s.label}
            </div>
          ))}
        </div>
      </div>

      {/* Alert */}
      <div className="px-5 pb-5">
        <div className="rounded-xl bg-[#FEEDED] border border-[#F5AAAA] px-4 py-2.5 text-xs font-medium text-[#A02020]">
          ⚠ Risque élevé — Plusieurs signaux de vigilance détectés. Évitez d'acheter sans vérification approfondie.
        </div>
      </div>
    </div>
  );
}

function LiveTicker() {
  // Duplicate for seamless loop
  const items = [...TICKER_ITEMS, ...TICKER_ITEMS];

  return (
    <div className="bg-[#0F3D5C] py-2.5 overflow-hidden border-t border-white/10">
      <div
        className="flex w-max"
        style={{ animation: "ticker 32s linear infinite" }}
      >
        {items.map((item, i) => (
          <span key={i} className="flex items-center gap-2 px-8 whitespace-nowrap text-xs text-white/70 font-light">
            <span
              className={`px-2 py-0.5 rounded font-semibold text-[11px] ${
                tickerColor(item.score) === "score-safe"
                  ? "bg-green-900/50 text-green-300"
                  : tickerColor(item.score) === "score-warn"
                  ? "bg-amber-900/40 text-amber-300"
                  : "bg-red-900/40 text-red-300"
              }`}
            >
              {item.score}/100
            </span>
            {item.url}
            <span className="text-white/25 mx-1">·</span>
          </span>
        ))}
      </div>
      <p className="text-center text-[10px] text-white/30 tracking-widest mt-1 font-light">
        ANALYSES RÉCENTES — EXEMPLES ILLUSTRATIFS
      </p>
      <style>{`
        @keyframes ticker {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

export default function HomePage() {
  return (
    <div className="min-h-screen flex flex-col page-enter">
      <Seo
        title="Vérifiez un site avant d'acheter"
        description="Outil français gratuit pour vérifier une boutique en ligne avant achat. Score de risque, signaux de vigilance et recommandations concrètes. Sans inscription, en quelques secondes."
        path="/"
        image={`${API_ORIGIN}/api/og/default.png`}
        keywords="vérifier site fiable, site arnaque, faux site e-commerce, boutique en ligne fiable, achat sécurisé, vérifier site avant achat"
        jsonLd={[
          organizationJsonLd(),
          websiteJsonLd(),
          webApplicationJsonLd(),
          faqJsonLd(FAQ_ITEMS),
        ]}
      />

      <Header />

      {/* ─── HERO ──────────────────────────────────────────────────────────── */}
      <section
        id="hero"
        className="bg-white border-b border-[#E4EAF0] pt-16 pb-16 relative overflow-hidden"
        data-testid="hero-section"
      >
        {/* Subtle grid */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#E4EAF0 1px, transparent 1px), linear-gradient(90deg, #E4EAF0 1px, transparent 1px)",
            backgroundSize: "40px 40px",
            opacity: 0.35,
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse 70% 55% at 50% -5%, rgba(15,61,92,0.06) 0%, transparent 70%)",
          }}
        />

        <div className="container-app text-center relative z-10">
          <span className="label-overline mb-5 flex items-center justify-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#22c55e] animate-pulse" />
            Outil gratuit de vérification avant achat
          </span>

          <h1
            className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#0E1621] tracking-tight mb-5 mt-4"
            style={{ fontFamily: "'Syne', sans-serif", letterSpacing: "-0.04em", lineHeight: 1.05 }}
          >
            Ce site est-il<br />
            <span style={{ color: "#0F3D5C" }}>vraiment fiable ?</span>
          </h1>

          <p
            className="text-base sm:text-lg text-[#3D5166] max-w-2xl mx-auto mb-2 leading-relaxed"
            style={{ fontWeight: 300 }}
          >
            Obtenez un score de risque clair, les signaux de confiance détectés
            et des recommandations concrètes. Gratuit, sans inscription, en quelques secondes.
          </p>
          <p className="text-sm text-[#6B8099] italic mb-10" style={{ fontWeight: 300 }}>
            Parce qu'un bon design ne garantit pas un bon vendeur.
          </p>

          <UrlForm large />

          <div className="flex flex-wrap items-center justify-center gap-5 mt-7">
            {[
              { icon: CheckCircle2, label: "Analyse gratuite",  color: "text-[#1A6641]" },
              { icon: Clock,        label: "Résultat immédiat", color: "text-[#0F3D5C]" },
              { icon: Lock,         label: "Sans inscription",  color: "text-[#3D5166]" },
            ].map(({ icon: Icon, label, color }) => (
              <div key={label} className="flex items-center gap-1.5 text-sm text-[#3D5166]" style={{ fontWeight: 300 }}>
                <Icon className={`w-3.5 h-3.5 ${color}`} />
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── LIVE TICKER ───────────────────────────────────────────────────── */}
      <LiveTicker />

      {/* ─── AD SLOT A — leaderboard just below the fold ───────────────────
           Best position: user just read the hero, high attention, pre-content.
           970×90 or 728×90 leaderboard. High CPM placement.            ──── */}
      {/* AD SLOT A REMOVED FOR ADSENSE */}

      {/* ─── TRUST STRIP ───────────────────────────────────────────────────── */}
      <section className="bg-[#F7F9FC] py-5 border-b border-[#E4EAF0]">
        <div className="container-app">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 max-w-4xl mx-auto">
            {[
              { icon: ShieldCheck, text: "17 signaux de vigilance analysés" },
              { icon: Scale,       text: "Inspiré des conseils publics de la DGCCRF" },
              { icon: Eye,         text: "Aide à la décision, pas une garantie" },
              { icon: Lock,        text: "Service indépendant, non affilié aux sites analysés" },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-2 text-sm text-[#3D5166]" style={{ fontWeight: 300 }}>
                <Icon className="w-3.5 h-3.5 text-[#0F3D5C] flex-shrink-0" />
                {text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SCORE DEMO ────────────────────────────────────────────────────── */}
      <section className="bg-white py-16 border-b border-[#E4EAF0]">
        <div className="container-app">
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="label-overline mb-3">Exemple de résultat</span>
              <h2 className="section-title mt-3 mb-4">Voyez ce qu'on détecte avant vous</h2>
              <p className="text-[#3D5166] text-sm leading-relaxed mb-6" style={{ fontWeight: 300 }}>
                Chaque analyse affiche un score de risque, les signaux détectés et une recommandation
                simple. Pas de jargon, uniquement les points utiles pour décider.
              </p>
              <div className="flex flex-col gap-2">
                {[
                  "Score de 0 (très suspect) à 100 (fiable)",
                  "Signaux légaux, techniques et de contact",
                  "Lecture simple du niveau de vigilance",
                  "Recommandations concrètes avant achat",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2.5 text-sm text-[#3D5166]" style={{ fontWeight: 300 }}>
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#1A6641] flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <ScorePreview />
          </div>
        </div>
      </section>

      {/* ─── BANNER SLOTS 1, 2 & 3 — hidden until real affiliate links are ready
           Uncomment and configure once partners are wired in.
           Ideal positions: slot 1 after demo, slots 2+3 after "why verify".   ── */}
      {/* <section className="bg-[#F7F9FC] py-10 border-b border-[#E4EAF0]">
        <div className="container-app">
          <Banner variant="primary" size="large" icon={Star}
            title="Ressource recommandée"
            description="..."
          />
        </div>
      </section> */}

      {/* ─── FEATURES GRID ─────────────────────────────────────────────────── */}
      <section className="bg-[#F7F9FC] py-16 border-b border-[#E4EAF0]">
        <div className="container-app">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="label-overline mb-3">Après le résultat</span>
            <h2 className="section-title mt-3 mb-4">Savoir lire un score, c'est bien. Savoir quoi faire ensuite, c'est mieux.</h2>
            <p className="text-[#3D5166] text-base leading-relaxed" style={{ fontWeight: 300 }}>
              Le score est un point de départ. Voici comment l'utiliser concrètement.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {[
              {
                icon: Search,
                title: "Recoupez les infos",
                text: "Cherchez le nom de la société sur Google. Un vrai vendeur laisse des traces : avis, presse, réseaux.",
              },
              {
                icon: FileText,
                title: "Lisez les mentions légales",
                text: "Un numéro SIRET, une adresse physique, un gérant identifiable — des éléments simples mais révélateurs.",
              },
              {
                icon: Lock,
                title: "Payez de façon sécurisée",
                text: "Privilégiez la carte bancaire ou PayPal. Évitez les virements et les paiements en crypto sur des sites inconnus.",
              },
              {
                icon: AlertTriangle,
                title: "En cas de doute, signalez",
                text: "Utilisez SignalConso pour alerter la DGCCRF. Votre signalement protège d'autres acheteurs.",
              },
            ].map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="card-base p-6"
              >
                <div className="w-9 h-9 rounded-xl bg-[#E8F1F8] flex items-center justify-center mb-4">
                  <Icon className="w-4 h-4 text-[#0F3D5C]" />
                </div>
                <h3 className="font-semibold text-[#0E1621] mb-2 text-sm" style={{ fontFamily: "'Syne', sans-serif" }}>
                  {title}
                </h3>
                <p className="text-sm text-[#3D5166] leading-relaxed" style={{ fontWeight: 300 }}>
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── AD SLOT B — mid-page rectangle ────────────────────────────────
           After features, before "how it works". User is engaged & scrolling.
           300×250 or 336×280 rectangle. Strong mid-funnel placement.   ──── */}
      {/* AD SLOT B REMOVED FOR ADSENSE */}

            {/* ─── COMPARISON TABLE ──────────────────────────────────────────────── */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-extrabold text-center text-gray-900 mb-2 tracking-tight">
            Ce que Fiable ou Pas vérifie différemment
          </h2>
          <p className="text-center text-gray-500 mb-10 text-base max-w-xl mx-auto">
            Une analyse gratuite pensée pour les consommateurs français : mentions légales, CGV,
            informations société et signaux de risque.
          </p>
          <div className="overflow-x-auto rounded-2xl shadow-lg border border-gray-200">
            <table className="w-full text-sm border-collapse bg-white">
              <thead>
                <tr>
                  <th className="text-left py-4 px-6 font-semibold text-gray-500 uppercase tracking-wider text-xs bg-gray-50 border-b border-gray-200 rounded-tl-2xl">
                    Fonctionnalité
                  </th>
                  <th className="py-4 px-6 font-bold text-white text-sm bg-blue-600 border-b border-blue-700">
                    ✦ Fiable ou Pas
                  </th>
                  <th className="py-4 px-6 font-semibold text-gray-500 text-xs uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                    ScamAdviser
                  </th>
                  <th className="py-4 px-6 font-semibold text-gray-500 text-xs uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                    Trustpilot
                  </th>
                  <th className="py-4 px-6 font-semibold text-gray-500 text-xs uppercase tracking-wider bg-gray-50 border-b border-gray-200 rounded-tr-2xl">
                    DGCCRF
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["🇫🇷 Signaux adaptés au droit français (CGV, mentions légales)", true, "partial", false, true],
                  ["Vérification de cohérence avec les données INSEE Sirene lorsque disponible", true, false, false, false],
                  ["Analyse automatique instantanée", true, true, false, false],
                  ["Détection phishing & malware", true, true, false, false],
                  ["100% gratuit, sans inscription", true, true, true, true],
                  ["Interface en français", true, true, false, true],
                ].map(([label, nous, scam, trust, dgccrf], i) => {
                  const renderStatus = (value) => {
                    if (value === "partial") {
                      return (
                        <span
                          title="Partiellement / selon les cas"
                          className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-100 text-amber-600 font-bold text-base"
                        >
                          ≈
                        </span>
                      );
                    }

                    return value
                      ? (
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-green-100 text-green-600 font-bold text-base">
                          ✓
                        </span>
                      )
                      : (
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-gray-400 font-bold text-base">
                          ✗
                        </span>
                      );
                  };

                  return (
                    <tr key={i} className={`transition-colors ${i % 2 === 0 ? "bg-white" : "bg-gray-50/60"} hover:bg-blue-50/30`}>
                      <td className="py-4 px-6 text-gray-800 font-medium border-b border-gray-100 text-sm">{label}</td>
                      <td className="py-4 px-6 text-center border-b border-blue-100 bg-blue-50/40">
                        {renderStatus(nous)}
                      </td>
                      <td className="py-4 px-6 text-center border-b border-gray-100">
                        {renderStatus(scam)}
                      </td>
                      <td className="py-4 px-6 text-center border-b border-gray-100">
                        {renderStatus(trust)}
                      </td>
                      <td className="py-4 px-6 text-center border-b border-gray-100">
                        {renderStatus(dgccrf)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 text-center mt-5">
            Comparaison indicative basée sur les fonctionnalités publiquement présentées par les services mentionnés au 30/04/2026.
            Fiable ou Pas n'est affilié à aucun des services mentionnés. Certaines fonctionnalités peuvent évoluer.
          </p>
        </div>
      </section>

      {/* ─── HOW IT WORKS ──────────────────────────────────────────────────── */}
      <section id="comment-ca-marche" className="bg-white py-16 border-b border-[#E4EAF0]">
        <div className="container-app">
          <div className="text-center mb-14">
            <span className="label-overline mb-3">Simple et rapide</span>
            <h2 className="section-title mt-3 mb-4">3 secondes. 3 étapes. 1 verdict.</h2>
            <p className="text-[#3D5166] text-base max-w-xl mx-auto" style={{ fontWeight: 300 }}>
              En quelques secondes, notre outil analyse les principaux indicateurs de confiance d'un site.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 max-w-4xl mx-auto relative">
            <div
              aria-hidden="true"
              className="hidden md:block absolute top-[28px] left-[calc(16.67%+16px)] right-[calc(16.67%+16px)] h-px bg-[#E4EAF0] z-0"
            />
            {[
              {
                step: "1", icon: Search,
                title: "Collez l'URL",
                desc: "Entrez l'adresse du site à vérifier, avec ou sans https://. On gère les deux.",
              },
              {
                step: "2", icon: ShieldCheck,
                title: "Analyse automatique",
                desc: "On analyse les signaux visibles : sécurité, légal, contact, contenu et identité.",
              },
              {
                step: "3", icon: CheckCircle2,
                title: "Résultat clair",
                desc: "Score, signaux détectés et recommandations simples. Vous décidez en connaissance de cause.",
              },
            ].map(({ step, icon: Icon, title, desc }) => (
              <div key={step} className="text-center px-6 relative z-10" data-testid="how-it-works-step">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#0F3D5C] text-white mb-5 shadow-md relative">
                  <Icon className="w-5 h-5" />
                  <span
                    className="absolute -top-2 -right-2 text-[10px] font-bold text-[#0F3D5C] bg-white border border-[#C8D4DF] rounded-full w-5 h-5 flex items-center justify-center shadow-sm"
                    style={{ fontFamily: "'Syne', sans-serif" }}
                  >
                    {step}
                  </span>
                </div>
                <h3 className="font-bold text-[#0E1621] mb-2 text-sm" style={{ fontFamily: "'Syne', sans-serif" }}>
                  {title}
                </h3>
                <p className="text-sm text-[#3D5166] leading-relaxed" style={{ fontWeight: 300 }}>
                  {desc}
                </p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/comment-ca-marche"
              className="inline-flex items-center gap-1.5 text-[#0F3D5C] text-sm font-medium hover:underline"
            >
              En savoir plus sur notre méthode <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── DISCLAIMER ────────────────────────────────────────────────────── */}
      <section className="bg-[#F7F9FC] py-10 border-b border-[#E4EAF0]">
        <div className="container-app max-w-3xl">
          <div className="bg-[#EEF4F8] border border-[#E4EAF0] rounded-2xl p-6 text-sm text-[#3D5166] leading-relaxed">
            <p className="font-semibold text-[#0E1621] mb-2" style={{ fontFamily: "'Syne', sans-serif" }}>
              Important
            </p>
            <p style={{ fontWeight: 300 }}>
              Fiable ou Pas est un <strong className="font-medium text-[#0E1621]">outil d'aide à la décision</strong>.
              Un score proche de 100 indique peu de signaux suspects. Un score proche de 0 indique plusieurs
              signaux de vigilance. Les résultats sont basés sur une analyse automatisée et doivent toujours
              être complétés par votre propre vigilance.
            </p>
          </div>
        </div>
      </section>

      {/* ─── WHY VERIFY ────────────────────────────────────────────────────── */}
      <section id="pourquoi-verifier" className="bg-white py-16 border-b border-[#E4EAF0]">
        <div className="container-app">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <span className="label-overline mb-3">Le contexte</span>
            <h2 className="section-title mt-3 mb-4">Parce que les arnaques ont un bon logo maintenant</h2>
            <p className="text-[#3D5166] text-base leading-relaxed" style={{ fontWeight: 300 }}>
              Les faux sites e-commerce sont de plus en plus crédibles. Promotions agressives,
              faux avis, design professionnel : quelques secondes de vérification peuvent éviter
              de sérieux problèmes après paiement.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {[
              {
                icon: AlertTriangle,
                bg: "bg-[#FFF3E0]", iconColor: "text-[#A05A00]",
                cardBg: "bg-[#FFFAF5]",
                title: "Sites frauduleux en hausse",
                text: "Certains faux sites imitent des boutiques connues, diffusent des publicités et disparaissent après les commandes.",
              },
              {
                icon: ShieldCheck,
                bg: "bg-[#E8F1F8]", iconColor: "text-[#0F3D5C]",
                cardBg: "bg-[#F5F9FC]",
                title: "Signaux souvent détectables",
                text: "Absence de mentions légales, coordonnées floues, CGV introuvables ou incohérences visibles sont des signaux utiles.",
              },
              {
                icon: FileText,
                bg: "bg-[#E5F4EE]", iconColor: "text-[#1A6641]",
                cardBg: "bg-[#F4FAF6]",
                title: "Informations légales à vérifier",
                text: "Une boutique sérieuse doit généralement afficher des informations claires sur son identité et ses conditions de vente.",
              },
              {
                icon: Clock,
                bg: "bg-[#F2F5F8]", iconColor: "text-[#3D5166]",
                cardBg: "bg-white",
                title: "30 secondes maintenant ou 3 semaines après",
                text: "Vérifier avant d'acheter prend moins de temps que gérer un litige, un remboursement impossible ou une commande jamais reçue.",
              },
            ].map(({ icon: Icon, bg, iconColor, cardBg, title, text }) => (
              <div
                key={title}
                className={`card-base p-6 ${cardBg} hover:shadow-md transition-shadow duration-200`}
                data-testid="why-card"
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-4 ${bg}`}>
                  <Icon className={`w-4 h-4 ${iconColor}`} />
                </div>
                <h3 className="font-semibold text-[#0E1621] mb-2 text-sm" style={{ fontFamily: "'Syne', sans-serif" }}>
                  {title}
                </h3>
                <p className="text-sm text-[#3D5166] leading-relaxed" style={{ fontWeight: 300 }}>
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── BANNER SLOTS 2 & 3 — hidden until real affiliate links are ready   ── */}
      {/* <section className="bg-white py-10 md:py-14 border-b border-[#E4EAF0]">
        <div className="container-app grid grid-cols-1 md:grid-cols-2 gap-4">
          <Banner variant="secondary" size="medium" icon={TrendingUp} title="Outil complémentaire" description="..." />
          <Banner variant="tertiary"  size="medium" icon={Shield}     title="Protection supplémentaire" description="..." />
        </div>
      </section> */}

      {/* ─── FAQ ───────────────────────────────────────────────────────────── */}
      <section id="faq" className="bg-white py-20 border-b border-[#E4EAF0]">
        <div className="container-app max-w-3xl">
          <div className="text-center mb-12">
            <span className="label-overline mb-3">Questions fréquentes</span>
            <h2 className="section-title mt-3 mb-4">Les vraies questions qu'on nous pose</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-2" data-testid="faq-accordion">
            {FAQ_ITEMS.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="card-base px-6 rounded-2xl border-[#E4EAF0]"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger
                  className="text-left font-semibold text-[#0E1621] py-4 hover:no-underline text-sm sm:text-base"
                  style={{ fontFamily: "'Syne', sans-serif" }}
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent
                  className="text-sm text-[#3D5166] leading-relaxed pb-4"
                  style={{ fontWeight: 300 }}
                >
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ─── AD SLOT C — footer leaderboard ────────────────────────────────
           Classic end-of-page position. User has read everything — high intent.
           728×90 or 970×250 billboard. Good for retargeting & remarketing.  ── */}
      {/* AD SLOT C REMOVED FOR ADSENSE */}

      {/* ─── CTA ───────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-[#0F3D5C] py-20">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="container-app text-center relative z-10">
          <p className="text-xs tracking-widest text-white/40 uppercase mb-4 font-light">Prêt à vérifier ?</p>
          <h2
            className="text-3xl sm:text-4xl font-extrabold text-white mb-4"
            style={{ fontFamily: "'Syne', sans-serif", letterSpacing: "-0.03em" }}
          >
            Avant d'acheter,<br />vérifiez le site.
          </h2>
          <p className="text-white/60 text-base mb-10 max-w-md mx-auto" style={{ fontWeight: 300 }}>
            Gratuit, sans inscription, en quelques secondes.
            Parce qu'un doute mérite une réponse.
          </p>
          <div className="max-w-xl mx-auto">
            <UrlForm light />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}